'use client';

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { IconClose } from './icons';

type ModalProps = {
    isOpen: boolean;
    onClose?: () => void;
    children: ReactNode;
    customClass?: string;
    ModalSize?: string;
    bg?: string;
    closePosition?: string;
    overflow?: string;
    closeColor?: string;
    modalPadding?: string;
    rounded?: string;
    modalId?: string;
    parentModalId?: string;
};

export default function Modal({
    isOpen,
    onClose,
    children,
    customClass,
    ModalSize,
    modalPadding,
    bg,
    closePosition,
    overflow,
    closeColor,
    rounded,
    parentModalId,
    modalId,
}: ModalProps) {
    const [fadeIn, setFadeIn] = useState(false);
    const modalContentRef = useRef<HTMLDivElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    const [closePos, setClosePos] = useState<string>('top-2 right-2');

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (backdropRef.current && event.target === backdropRef.current) {
                onClose?.();
            }
        }

        if (isOpen) {
            document.addEventListener('pointerdown', handleClickOutside);
        }

        if (closePosition) {
            setClosePos(`absolute ${closePosition}`);
        } else {
            setClosePos('absolute top-2 right-2');
        }

        return () => {
            document.removeEventListener('pointerdown', handleClickOutside);
        };
    }, [isOpen, onClose, closePosition]);

    useEffect(() => {
        const parentModal = document.querySelector(`#${parentModalId}`) as HTMLElement;
        if (isOpen) {
            if (parentModal) {
                parentModal.scrollTo({ top: 0, behavior: 'smooth' });
                parentModal.style.overflow = 'hidden';
            }
            requestAnimationFrame(() => setFadeIn(true));
        } else {
            if (parentModal) {
                parentModal.style.overflow = '';
            }
            setFadeIn(false);
        }
    }, [isOpen, parentModalId]);

    if (!isOpen) return null;

    return (
        <div
            ref={backdropRef}
            className={`${customClass} inset-x-0 top-0 bottom-0 z-50 transition-all ease-in-outpy-3 fixed inset-0 rounded-lg bg-opacity-90 flex justify-center duration-700 ${fadeIn ? 'bg-black/79' : 'bg-black/0'}`}
        >
            <div
                id={`${modalId || ''}`}
                ref={modalContentRef}
                className={`${ModalSize} ${bg ? bg : 'bg-[#121212]'} top-1/2 bottom-1/2 -translate-y-1/2  text-white ${modalPadding ? modalPadding : 'p-6'} ${rounded ? rounded : 'rounded-xl'} w-full relative size-fit ${overflow ? overflow : 'overflow-y-auto'} `}
            >
                <div className={`${closePos} text-white text-xl`}>
                    <IconClose
                        className={`cursor-pointer ${closeColor ? `text-[${closeColor}]` : 'text-white'}`}
                        onClick={onClose}
                    />
                </div>
                {children}
            </div>
        </div>
    );
}
